import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { CommentsField } from "@/shared/fields/CommentsField";
import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import {
  Autocomplete,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  ClickAwayListener,
  Box,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BucketAutocomplete } from "@/shared/autocompletes/BucketAutocomplete";
import { CHARGE_STATUS_OPTIONS } from "@/pages/sy/constants/chargeStatus";
import { useBucketsQuery } from "@/pages/sy/hooks/queries/useBucketsQuery";
import { useRecipesQuery } from "@/pages/sy/hooks/queries/useRecipesQuery";
import { useCommoditiesQuery } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import { useRecipeLayersQuery } from "@/pages/sy/hooks/queries/useRecipeLayersQuery";
import { useChargeColorsAndWeights } from "@/pages/sy/hooks/useColorsAndWeight";
import { useSYOperators } from "@/pages/sy/hooks/useSYOperators";
import { Charge, CHARGE_INITIAL_VALUES } from "@/pages/sy/models/Charge";
import { upsertBucket } from "@/pages/sy/repositories/ChargeRepository";
import { useChargeLayersQuery } from "@/pages/sy/hooks/queries/useChargeLayersQuery";
import {
  useUpsertChargeMutation,
  useUpsertChargeLayerMutation,
  useDeleteChargeLayerMutation,
} from "@/pages/sy/hooks/queries/useChargeMutations";
import { getErrorMessage } from "@/shared/hooks/getErrorMessage";
import { LayerGroupBox } from "@/pages/sy/components/LayerGroupBox";
import { LayerMoveDialog } from "@/pages/sy/components/LayerMoveDialog";
import { useChargeLayerMove } from "../../hooks/useChargeLayerMove";
import {
  getSiblingCommodityIds,
  toTopFirstOrder,
} from "@/pages/sy/utils/layerGroups";
import {
  EDITABLE_GROUP_HEADER,
  READ_ONLY_GROUP_HEADER,
  getLayerGroupFrames,
} from "../../utils/layerGroupFrames";
import { ChargeLayerComponent } from "./components/ChargeLayer";
import { ReadOnlyLayerRow } from "./components/ReadOnlyLayerRow";
import { ChangeStatusScheduledModal } from "./modals/ChangeStatusScheduledModal";
import { ChargeLayerModal } from "./modals/ChargeLayerModal";

type Props = {
  charge: Charge | null;
  modal: "new" | "edit" | "delete" | "";
  refetchInterval: number;
  onHide: (params: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => void;
};

export const ChargeDetails = ({
  charge,
  modal,
  refetchInterval,
  onHide,
}: Props) => {
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [currentCharge, setCurrentCharge] = useState<Charge | null>(null);
  const [showStatusChangeConfirmation, setShowStatusChangeConfirmation] =
    useState<boolean>(false);
  const [pendingSave, setPendingSave] = useState<Charge | null>(null);
  const [isNewCharge, setIsNewCharge] = useState<boolean>(false);

  const [bucketsSelected, setBucketsSelected] = useState<boolean>(false);
  const [showChargeLayerModal, setShowChargeLayerModal] =
    useState<boolean>(false);
  const [isEditingCapacity, setIsEditingCapacity] = useState(false);
  const [editedCapacity, setEditedCapacity] = useState<number | null>(null);
  const [isUpdatingCapacity, setIsUpdatingCapacity] = useState(false);
  const [isEditingScheduledHeat, setIsEditingScheduledHeat] = useState(false);
  const [editedScheduledHeat, setEditedScheduledHeat] = useState<string | null>(
    null,
  );
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [pendingRecipe, setPendingRecipe] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [confirmMessage, setConfirmMessage] = useState<string>("");

  const bucketCapacityRef = useRef<HTMLInputElement>(null);
  const scheduledHeatRef = useRef<HTMLInputElement>(null);

  // Custom hooks
  const queryClient = useQueryClient();
  const { data: buckets = [] } = useBucketsQuery();
  const { data: recipes = [] } = useRecipesQuery(true, true);
  const { data: commodities = [] } = useCommoditiesQuery();

  // === TanStack Query Mutations ===
  const upsertChargeMutation = useUpsertChargeMutation();
  const upsertChargeLayerMutation = useUpsertChargeLayerMutation();
  const deleteChargeLayerMutation = useDeleteChargeLayerMutation();

  const onMutationError = (err: unknown) => setError(getErrorMessage(err));

  // Combined loading state for UI (replaces manual isLoading state)
  const isMutating =
    upsertChargeMutation.isPending ||
    upsertChargeLayerMutation.isPending ||
    deleteChargeLayerMutation.isPending;
  const {
    syOperators,
    error: SYerror,
    setError: SYSetError,
  } = useSYOperators();

  // === TanStack Query: Charge Layers ===
  const { data: rawChargeLayers = [] } = useChargeLayersQuery(
    charge?.ChargeId,
    refetchInterval,
  );

  const chargeLayers = useMemo(
    () => toTopFirstOrder(rawChargeLayers),
    [rawChargeLayers],
  );

  const totalWeight = useMemo(
    () => rawChargeLayers.reduce((sum, layer) => sum + (layer.Weight || 0), 0),
    [rawChargeLayers],
  );

  const [newLayerId, setNewLayerId] = useState<number | null>(null);

  const resetNewLayerId = useCallback(() => {
    setNewLayerId(null);
  }, []);

  const {
    setBucketCapacity,
    uiCapacity,
    setUiCapacity,
    getCommodityColor,
    getRecipeLayerPercentage,
    getBgColorWithAlpha,
  } = useChargeColorsAndWeights();

  // Form control
  const { reset, getValues, control, setValue, watch } = useForm<Charge>({
    mode: "onChange",
    defaultValues: CHARGE_INITIAL_VALUES,
  });

  // Watched values
  const bucketId = watch("BucketId");
  const recipeId = watch("RecipeId");
  const chargeNumber = watch("ChargeNumber");
  const isChargedIntoEAF = charge?.ChargeStatusCode === "H";

  // === TanStack Query: Recipe Layers ===
  const { data: rawRecipeLayers = [] } = useRecipeLayersQuery(
    recipeId,
    chargeNumber || 1,
  );

  const recipeLayers = useMemo(
    () => toTopFirstOrder(rawRecipeLayers),
    [rawRecipeLayers],
  );

  const chargeGroupFrames = useMemo(
    () =>
      getLayerGroupFrames(
        chargeLayers,
        "ChargeLayerId",
        isChargedIntoEAF ? READ_ONLY_GROUP_HEADER : EDITABLE_GROUP_HEADER,
      ),
    [chargeLayers, isChargedIntoEAF],
  );

  const recipeGroupFrames = useMemo(
    () =>
      getLayerGroupFrames(
        recipeLayers,
        "RecipeLayerId",
        READ_ONLY_GROUP_HEADER,
      ),
    [recipeLayers],
  );

  const recipeTotalWeight = useMemo(
    () => rawRecipeLayers.reduce((sum, layer) => sum + (layer.Weight || 0), 0),
    [rawRecipeLayers],
  );

  // Derived values
  const enabledRecipes = useMemo(
    () => recipes.filter((r) => r.IsEnable),
    [recipes],
  );
  const maxLayerNumber = useMemo(
    () =>
      chargeLayers.length > 0
        ? Math.max(...chargeLayers.map((layer) => layer.LayerNumber))
        : 0,
    [chargeLayers],
  );
  const { requestMove, dialogProps: layerMoveDialogProps } = useChargeLayerMove(
    {
      layers: chargeLayers,
      setNotificationMessage,
      onError: onMutationError,
    },
  );
  const isBucketCapacityExceeded = uiCapacity > 0 && totalWeight > uiCapacity;
  const isTargetWeightExceeded =
    recipeTotalWeight > 0 && totalWeight > recipeTotalWeight;
  const showBucketWarning = isBucketCapacityExceeded;
  const showTargetWarning = !showBucketWarning && isTargetWeightExceeded;
  const isAddLayerDisabled = modal !== "edit" || !charge?.ChargeId;

  const handleBucketChange = useCallback(
    (newBucketId: number) => {
      if (!newBucketId || newBucketId === 0) {
        setBucketCapacity(0);
        setUiCapacity(0);
        setEditedCapacity(null);
        return;
      }

      const selectedBucket = buckets.find((b) => b.BucketId === newBucketId);
      if (selectedBucket) {
        const capacity = selectedBucket.Capacity || null;
        setBucketCapacity(capacity || 0);
        // Only update UI capacity if we're not in the middle of updating capacity
        if (!isUpdatingCapacity) {
          setUiCapacity(capacity || 0);
          setEditedCapacity(capacity);
        }
      }

      if (isNewCharge && newBucketId !== 0) {
        setBucketsSelected(true);
      }
    },
    [
      buckets,
      isNewCharge,
      isUpdatingCapacity,
      setBucketCapacity,
      setUiCapacity,
    ],
  );

  useEffect(() => {
    handleBucketChange(bucketId);
  }, [bucketId, handleBucketChange]);

  // isBucketCapacityExceeded (derived above) replaces the old setIsWeightExceeded pattern
  // Recipe layers are loaded automatically by useRecipeLayersQuery (enabled: !!recipeId)

  // Only reset form when modal opens or when switching to a different charge
  // This prevents losing user edits when polling refetches the same charge
  const initializeForm = useCallback(() => {
    const chargeData = charge || CHARGE_INITIAL_VALUES;
    reset(chargeData);
    setCurrentCharge(chargeData);
    setIsNewCharge(modal === "new");

    if (modal === "new") {
      setUiCapacity(0);
      setBucketCapacity(0);
      setBucketsSelected(false);
    } else if (charge?.ChargeId) {
      // Layers are loaded automatically by useChargeLayersQuery (enabled: !!chargeId)
      setBucketsSelected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    charge?.ChargeId,
    modal,
    reset,
    setValue,
    setUiCapacity,
    setBucketCapacity,
  ]);

  useEffect(() => {
    if (modal !== "") {
      initializeForm();
    }
  }, [modal, initializeForm]);

  useEffect(() => {
    setEditedCapacity(uiCapacity);
  }, [uiCapacity]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingCapacity && bucketCapacityRef.current) {
      bucketCapacityRef.current.focus();
      bucketCapacityRef.current.select();
    }
  }, [isEditingCapacity]);

  useEffect(() => {
    if (isEditingScheduledHeat && scheduledHeatRef.current) {
      scheduledHeatRef.current.focus();
      scheduledHeatRef.current.select();
    }
  }, [isEditingScheduledHeat]);

  // Complex operation with multiple sequential steps - uses mutateAsync
  const saveCharge = async (
    data: Charge,
    skipConfirmation: boolean = false,
  ) => {
    if (
      !skipConfirmation &&
      data.ChargeStatusCode === "S" &&
      currentCharge?.ChargeStatusCode !== "S" &&
      chargeLayers.length > 0
    ) {
      setPendingSave(data);
      setShowStatusChangeConfirmation(true);
      return;
    }

    try {
      const isCreatingNew = !data.ChargeId;
      const chargeData = {
        ...data,
        ChargeId: data.ChargeId,
        TotalWeight: totalWeight,
      };

      const chargeResp = await upsertChargeMutation.mutateAsync(chargeData);

      const entityId =
        chargeResp.data?.tables?.[0]?.rows?.[0]?.ChargeId || data.ChargeId;
      const layersDeletedCount =
        chargeResp.data?.tables?.[0]?.rows?.[0]?.LayersDeleted || 0;

      if (!entityId) {
        throw new Error("Failed to get charge ID after save");
      }

      const updatedCharge = { ...data, ChargeId: entityId };

      if (isCreatingNew) {
        const secondUpsertData = { ...updatedCharge };
        await upsertChargeMutation.mutateAsync(secondUpsertData);
      }

      reset(updatedCharge);
      setCurrentCharge(updatedCharge);
      setBucketsSelected(true);

      setNotificationMessage(
        layersDeletedCount > 0
          ? `Charge saved successfully. ${layersDeletedCount} layer(s) were deleted due to status change.`
          : "Charge saved successfully",
      );

      onHide({
        shouldUpdate: true,
        close: false,
        entityId,
      });
    } catch (err) {
      onMutationError(err);
    }
  };

  // Events
  const handleFieldChange = async (
    fieldName: keyof Charge,
    value: Charge[keyof Charge],
  ) => {
    setValue(fieldName, value);

    if (fieldName === "BucketId") {
      handleBucketChange(value as number);
    }

    const currentValues = getValues();
    await saveCharge({ ...currentValues, [fieldName]: value });
  };

  const handleAddNewLayer = () => {
    if (!charge?.ChargeId) return;
    setShowChargeLayerModal(true);
  };

  const handleConfirmStatusChange = () => {
    if (pendingSave) {
      setShowStatusChangeConfirmation(false);
      saveCharge(pendingSave, true);
      setPendingSave(null);
    }
  };

  const handleCancelStatusChange = () => {
    setShowStatusChangeConfirmation(false);
    setPendingSave(null);

    if (currentCharge) {
      reset({
        ...currentCharge,
      });
    }
  };

  const handleSaveNewLayerModal = (
    commodityId: number | null,
    layerNumber: number | null,
  ) => {
    if (!charge?.ChargeId || !commodityId) {
      setError("Missing charge ID or commodity ID");
      return;
    }

    resetNewLayerId();

    const maxLayerNumber =
      chargeLayers.length > 0
        ? Math.max(...chargeLayers.map((layer) => layer.LayerNumber))
        : 0;

    const newLayer = {
      ChargeId: charge.ChargeId,
      LayerNumber: layerNumber ?? maxLayerNumber + 1,
      CommodityId: commodityId,
      Weight: 0,
      IsCurrent: false,
      IsWeightManual: true,
    };

    upsertChargeLayerMutation.mutate(newLayer, {
      onSuccess: (resp) => {
        const createdLayerId = resp.data?.tables?.[0]?.rows?.[0]?.Id;
        if (createdLayerId) {
          setNewLayerId(createdLayerId);
        }
        setNotificationMessage(
          layerNumber == null
            ? "New layer added successfully"
            : `Material added to layer ${layerNumber}`,
        );
        setShowChargeLayerModal(false);
      },
      onError: onMutationError,
    });
  };

  const handleCancelNewLayerAction = (layerId: number) => {
    deleteChargeLayerMutation.mutate(layerId, {
      onSuccess: () => {
        setNewLayerId(null);
        setNotificationMessage("Layer creation cancelled");
      },
      onError: onMutationError,
    });
  };

  const handleEditComplete = useCallback(() => {
    resetNewLayerId();
  }, [resetNewLayerId]);

  const handleOnClickAwayBucketCapacity = async () => {
    if (!isEditingCapacity || bucketId === 0) return;
    const newCapacity = editedCapacity || 0;
    setIsEditingCapacity(false);
    if (bucketId && newCapacity !== null && uiCapacity !== newCapacity) {
      try {
        setIsUpdatingCapacity(true);
        setUiCapacity(newCapacity);
        setBucketCapacity(newCapacity);
        await upsertBucket(bucketId, newCapacity);
        setNotificationMessage("Bucket capacity updated successfully.");
        queryClient.invalidateQueries({ queryKey: ["buckets"] });
      } catch (error) {
        setError("Failed to update bucket capacity.");
        // Revert UI state on error
        setUiCapacity(uiCapacity);
        setEditedCapacity(uiCapacity);
      } finally {
        setIsUpdatingCapacity(false);
      }
    }
  };

  const handleOnClickAwayScheduledHeat = () => {
    if (!isEditingScheduledHeat || !charge?.ChargeId) return;
    const newScheduledHeat = editedScheduledHeat ?? "";
    setIsEditingScheduledHeat(false);
    if (
      charge.ScheduledHeatName !== newScheduledHeat &&
      newScheduledHeat !== null
    ) {
      const updatedCharge = {
        ...charge,
        ScheduledHeatName: newScheduledHeat,
      };
      upsertChargeMutation.mutate(updatedCharge, {
        onSuccess: () => {
          setNotificationMessage("Scheduled Heat updated successfully.");
          setCurrentCharge({
            ...currentCharge,
            ScheduledHeatName: newScheduledHeat,
            BucketId: currentCharge?.BucketId ?? 0,
            ChargeStatusCode: currentCharge?.ChargeStatusCode ?? "S",
          });
        },
        onError: onMutationError,
      });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        overflow: { xs: "auto", md: "hidden" },
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {charge === null && modal !== "new" ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>Select a charge or create a new one</Typography>
        </Box>
      ) : (
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: { xs: "auto", md: "hidden" },
          }}
        >
          {/* Section One: Input Fields */}
          <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <Grid container spacing={1}>
                  {/* Bucket */}
                  <Grid item md={4} xs={12}>
                    <Controller
                      name="BucketId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState }) => (
                        <BucketAutocomplete
                          label="Bucket"
                          value={field.value}
                          onChange={(value) => {
                            handleFieldChange("BucketId", value);
                          }}
                          fieldError={fieldState.error}
                          disabled={modal === "delete"}
                        />
                      )}
                    />
                  </Grid>

                  {/* Bucket Capacity */}
                  <Grid item md={4} xs={12}>
                    <ClickAwayListener
                      onClickAway={handleOnClickAwayBucketCapacity}
                    >
                      <div>
                        <TextField
                          inputRef={bucketCapacityRef}
                          label="Bucket Capacity"
                          variant="outlined"
                          value={
                            isEditingCapacity
                              ? editedCapacity || ""
                              : uiCapacity || ""
                          }
                          size="small"
                          fullWidth
                          disabled={!isEditingCapacity}
                          type="number"
                          error={
                            uiCapacity < recipeTotalWeight &&
                            buckets.find((b) => b.BucketId === bucketId)
                              ?.BucketName !== "Unknown".trim()
                          }
                          helperText={
                            uiCapacity < recipeTotalWeight &&
                            buckets.find((b) => b.BucketId === bucketId)
                              ?.BucketName !== "Unknown".trim()
                              ? `Capacity should be greater than ${
                                  recipeTotalWeight ||
                                  currentCharge?.ManualTargetWeight ||
                                  0
                                }`
                              : ""
                          }
                          slotProps={{
                            input: {
                              readOnly: !isEditingCapacity,
                              endAdornment: (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setIsEditingCapacity(!isEditingCapacity);
                                  }}
                                  disabled={modal === "delete"}
                                  tabIndex={-1}
                                  color={
                                    isEditingCapacity ? "default" : "primary"
                                  }
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              ),
                            },
                          }}
                          onChange={(e) => {
                            if (isEditingCapacity) {
                              setEditedCapacity(Number(e.target.value));
                            }
                          }}
                          sx={
                            isEditingCapacity
                              ? {
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: (theme) =>
                                      `${theme.palette.primary.main} !important`,
                                    borderWidth: "2px !important",
                                  },
                                }
                              : undefined
                          }
                        />
                      </div>
                    </ClickAwayListener>
                  </Grid>

                  {/* Status */}
                  <Grid item md={4} xs={12}>
                    <Controller
                      name="ChargeStatusCode"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          disableClearable
                          options={CHARGE_STATUS_OPTIONS}
                          getOptionLabel={(option) => option.label}
                          value={
                            CHARGE_STATUS_OPTIONS.find(
                              (opt) => opt.value === field.value,
                            ) ?? CHARGE_STATUS_OPTIONS[0]
                          }
                          onChange={(_, newValue) => {
                            handleFieldChange(
                              "ChargeStatusCode",
                              newValue?.value || "",
                            );
                          }}
                          disabled={
                            modal === "delete" ||
                            (isNewCharge && !bucketsSelected)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Status"
                              variant="outlined"
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  {/* Created Time */}
                  {!isNewCharge && (
                    <Grid item md={4} xs={12}>
                      <Controller
                        name="CreationTimestamp"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label="Created"
                            variant="outlined"
                            value={
                              dxtToLocalServerTime(
                                field.value,
                                "MM/dd/yy HH:mm",
                              ) ?? ""
                            }
                            size="small"
                            fullWidth
                            disabled
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {/* Operator */}
                  <Grid item md={4} xs={12}>
                    <Controller
                      name="OperatorId"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={syOperators}
                          getOptionLabel={(option) => option.OperatorName}
                          value={
                            syOperators.find(
                              (op) => op.OperatorId === field.value,
                            ) || null
                          }
                          onChange={(_, newValue) => {
                            handleFieldChange(
                              "OperatorId",
                              newValue?.OperatorId || null,
                            );
                          }}
                          disabled
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Operator"
                              variant="outlined"
                              size="small"
                              fullWidth
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  {/* Scheduled Heat */}
                  <Grid item md={4} xs={12}>
                    <ClickAwayListener
                      onClickAway={handleOnClickAwayScheduledHeat}
                    >
                      <div>
                        <TextField
                          inputRef={scheduledHeatRef}
                          label="Scheduled Heat"
                          variant="outlined"
                          value={
                            isEditingScheduledHeat
                              ? (editedScheduledHeat ?? "")
                              : (currentCharge?.ScheduledHeatName ?? "")
                          }
                          size="small"
                          fullWidth
                          disabled={!isEditingScheduledHeat}
                          type="text"
                          slotProps={{
                            input: {
                              readOnly: !isEditingScheduledHeat,
                              endAdornment: (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (!isEditingScheduledHeat) {
                                      setEditedScheduledHeat(
                                        currentCharge?.ScheduledHeatName ?? "",
                                      );
                                    }
                                    setIsEditingScheduledHeat(
                                      !isEditingScheduledHeat,
                                    );
                                  }}
                                  disabled={modal === "delete"}
                                  tabIndex={-1}
                                  color={
                                    isEditingScheduledHeat
                                      ? "default"
                                      : "primary"
                                  }
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              ),
                            },
                          }}
                          onChange={(e) => {
                            if (isEditingScheduledHeat) {
                              setEditedScheduledHeat(e.target.value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleOnClickAwayScheduledHeat();
                            }
                          }}
                          sx={
                            isEditingScheduledHeat
                              ? {
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: (theme) =>
                                      `${theme.palette.primary.main} !important`,
                                    borderWidth: "2px !important",
                                  },
                                }
                              : undefined
                          }
                        />
                      </div>
                    </ClickAwayListener>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item md={6} xs={12}>
                <Grid container spacing={1}>
                  {/* Recipe */}
                  <Grid item md={4} xs={12}>
                    <Controller
                      name="RecipeId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field, fieldState }) => {
                        const recipeOptions = [
                          { RecipeId: null, RecipeName: "Unlinked" },
                          ...enabledRecipes,
                        ];
                        return (
                          <Autocomplete
                            options={recipeOptions}
                            getOptionLabel={(option) =>
                              option.RecipeName || "Unlinked"
                            }
                            value={
                              recipeOptions.find(
                                (r) => r.RecipeId === field.value,
                              ) || null
                            }
                            onChange={(_, newValue) => {
                              const value = newValue?.RecipeId ?? null;
                              if (
                                value !== null &&
                                value !== field.value &&
                                field.value !== null &&
                                field.value !== undefined
                              ) {
                                const hasChargeLayersWithWeight =
                                  chargeLayers.some(
                                    (layer) => layer.Weight !== 0,
                                  );
                                if (
                                  charge?.ChargeStatusCode === "O" &&
                                  hasChargeLayersWithWeight
                                ) {
                                  const selectedRecipe = enabledRecipes.find(
                                    (r) => r.RecipeId === value,
                                  );
                                  setPendingRecipe({
                                    id: Number(value),
                                    name: selectedRecipe?.RecipeName || "",
                                  });
                                  setConfirmMessage(
                                    "Updating the recipe will automatically reset the details of the current layers: commodities and weights will be replaced by those from the new recipe. Do you want to continue?",
                                  );
                                  setShowConfirmModal(true);
                                } else {
                                  handleFieldChange("RecipeId", value);
                                }
                              } else {
                                handleFieldChange("RecipeId", value);
                              }
                            }}
                            disabled={
                              modal === "delete" ||
                              (isNewCharge && !bucketsSelected) ||
                              charge?.ChargeStatusCode === "C" ||
                              charge?.ChargeStatusCode === "H"
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Recipe"
                                variant="outlined"
                                size="small"
                                fullWidth
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        );
                      }}
                    />
                  </Grid>

                  {!isNewCharge && (
                    <Grid item md={4} xs={12}>
                      <Controller
                        name="ManualTargetWeight"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            value={field.value || ""}
                            variant="outlined"
                            fullWidth
                            size="small"
                            label="Target Weight"
                            disabled
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Section Two: Charge Layers and Recipe Layers */}
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 2,
              flex: { md: 1 },
              minHeight: { md: 0 },
            }}
          >
            <Grid
              container
              columnSpacing={2}
              rowSpacing={{ xs: 2, md: 0 }}
              sx={{ height: { md: "100%" } }}
            >
              <Grid item md={6} xs={12} sx={{ height: { md: "100%" } }}>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    position: "relative",
                    height: { xs: "auto", md: "100%" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: "10px 15px",
                      bgcolor: "grey.100",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="h6">Charge Layers</Typography>
                    {!isChargedIntoEAF && (
                      <IconButton
                        onClick={handleAddNewLayer}
                        color="primary"
                        sx={{
                          p: 0,
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                        }}
                        disabled={
                          isAddLayerDisabled ||
                          (isNewCharge && !bucketsSelected)
                        }
                        title="Add new layer"
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Grid
                    container
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Grid
                      item
                      md={2}
                      xs={2}
                      sx={{
                        p: isChargedIntoEAF ? "4px 15px" : "4px 8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption">Layer</Typography>
                    </Grid>
                    <Grid
                      item
                      md={isChargedIntoEAF ? 4 : 5}
                      xs={isChargedIntoEAF ? 4 : 5}
                      sx={{
                        p: isChargedIntoEAF ? "4px 15px" : "4px 8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption">Commodity</Typography>
                    </Grid>
                    {isChargedIntoEAF && (
                      <Grid
                        item
                        md={3}
                        xs={3}
                        sx={{ p: "4px 15px", textAlign: "center" }}
                      >
                        <Typography variant="caption">%</Typography>
                      </Grid>
                    )}
                    <Grid
                      item
                      md={3}
                      xs={3}
                      sx={{
                        p: isChargedIntoEAF ? "4px 15px" : "4px 8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption">Weight</Typography>
                    </Grid>
                    {!isChargedIntoEAF && (
                      <Grid
                        item
                        md={2}
                        xs={2}
                        sx={{ p: "4px 8px", textAlign: "center" }}
                      />
                    )}
                  </Grid>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: { xs: "450px", md: 0 },
                      maxHeight: { xs: "450px", md: "none" },
                      overflow: "auto",
                      bgcolor: isNewCharge ? "grey.50" : "background.paper",
                    }}
                  >
                    {chargeLayers.length === 0 || modal === "new" ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          p: "40px 0",
                          color: "text.secondary",
                        }}
                      >
                        {isNewCharge ? (
                          <Typography variant="body2">
                            Save the charge first to add layers
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            No layers to show
                          </Typography>
                        )}
                      </Box>
                    ) : isChargedIntoEAF ? (
                      chargeLayers.map((layer) => {
                        const commodityColor = getCommodityColor(
                          layer.CommodityId as number,
                          commodities,
                        );
                        const groupFrame = chargeGroupFrames.get(
                          layer.ChargeLayerId,
                        );

                        return (
                          <LayerGroupBox
                            key={`layer-${layer.ChargeLayerId}`}
                            frame={groupFrame}
                          >
                            <ReadOnlyLayerRow
                              layerNumber={layer.LayerNumber}
                              commodityName={layer.CommodityName}
                              weight={layer.Weight}
                              percentage={getRecipeLayerPercentage(
                                layer.Weight,
                                totalWeight,
                              )}
                              commodityColor={commodityColor}
                              bgColor={getBgColorWithAlpha(
                                commodityColor,
                                0.15,
                              )}
                              isLastInGroup={groupFrame?.position === "last"}
                            />
                          </LayerGroupBox>
                        );
                      })
                    ) : (
                      chargeLayers.map((layer) => {
                        const groupFrame = chargeGroupFrames.get(
                          layer.ChargeLayerId,
                        );

                        return (
                          <LayerGroupBox
                            key={`layer-${layer.ChargeLayerId}-${layer.LayerNumber}`}
                            frame={groupFrame}
                          >
                            <ChargeLayerComponent
                              layer={layer}
                              commodities={commodities}
                              disabled={false}
                              setNotificationMessage={setNotificationMessage}
                              initialEditMode={
                                layer.ChargeLayerId === newLayerId
                              }
                              onEditComplete={handleEditComplete}
                              isNewLayer={layer.ChargeLayerId === newLayerId}
                              onCancelNewLayer={handleCancelNewLayerAction}
                              totalWeight={totalWeight}
                              maxSelectableLayerNumber={
                                maxLayerNumber + (groupFrame ? 1 : 0)
                              }
                              onLayerNumberChange={requestMove}
                              siblingCommodityIds={getSiblingCommodityIds(
                                chargeLayers,
                                layer,
                                "ChargeLayerId",
                              )}
                              isLastInGroup={groupFrame?.position === "last"}
                            />
                          </LayerGroupBox>
                        );
                      })
                    )}
                  </Box>
                  <Grid
                    container
                    sx={{
                      borderTop: 1,
                      borderColor: "divider",
                      bgcolor: "grey.50",
                      p: "8px 15px",
                      alignItems: "center",
                    }}
                  >
                    <Grid item md={8} xs={12} sx={{ pr: 2 }}>
                      {showBucketWarning && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "error.main",
                            display: "block",
                            mb: showTargetWarning ? 0.5 : 0,
                          }}
                        >
                          Bucket weight overhead:{" "}
                          {uiCapacity > 0
                            ? Math.max(0, totalWeight - uiCapacity)
                            : 0}
                        </Typography>
                      )}
                      {showTargetWarning && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "error.main",
                            display: "block",
                          }}
                        >
                          Target weight overhead:{" "}
                          {recipeTotalWeight > 0
                            ? Math.max(0, totalWeight - recipeTotalWeight)
                            : 0}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item md={4} xs={12} sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            showBucketWarning || showTargetWarning
                              ? "error.main"
                              : "inherit",
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        Total: {totalWeight} / {charge?.AimChargeWeight || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item md={6} xs={12} sx={{ height: { md: "100%" } }}>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    position: "relative",
                    height: { xs: "auto", md: "100%" },
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      p: "10px 15px",
                      bgcolor: "grey.100",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    Recipe Layers
                  </Typography>
                  <Grid
                    container
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Grid
                      item
                      md={2}
                      xs={2}
                      sx={{
                        p: "4px 15px",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="caption">Layer</Typography>
                    </Grid>
                    <Grid
                      item
                      md={4}
                      xs={4}
                      sx={{ p: "4px 15px", textAlign: "center" }}
                    >
                      <Typography variant="caption">Commodity</Typography>
                    </Grid>
                    <Grid
                      item
                      md={3}
                      xs={3}
                      sx={{ p: "4px 15px", textAlign: "center" }}
                    >
                      <Typography variant="caption">%</Typography>
                    </Grid>
                    <Grid
                      item
                      md={3}
                      xs={3}
                      sx={{ p: "4px 15px", textAlign: "center" }}
                    >
                      <Typography variant="caption">Weight</Typography>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: { xs: "450px", md: 0 },
                      maxHeight: { xs: "450px", md: "none" },
                      overflow: "auto",
                      bgcolor: "background.paper",
                    }}
                  >
                    {recipeLayers.length === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          p: "40px 0",
                          color: "text.secondary",
                        }}
                      >
                        <Typography variant="body2">
                          {recipeId
                            ? "No recipe layers found"
                            : "Select a recipe to view layers"}
                        </Typography>
                      </Box>
                    ) : (
                      recipeLayers.map((layer) => {
                        const commodityFromAll = commodities.find(
                          (c) => c.CommodityId === layer.CommodityId,
                        );
                        const isDisabled =
                          commodityFromAll && !commodityFromAll.IsEnable;
                        const commodityColor = getCommodityColor(
                          layer.CommodityId as number,
                          commodities,
                        );
                        const groupFrame = recipeGroupFrames.get(
                          layer.RecipeLayerId,
                        );

                        return (
                          <LayerGroupBox
                            key={`recipe-layer-${layer.RecipeLayerId}`}
                            frame={groupFrame}
                          >
                            <ReadOnlyLayerRow
                              layerNumber={layer.LayerNumber}
                              commodityName={layer.CommodityName}
                              weight={layer.Weight}
                              percentage={getRecipeLayerPercentage(
                                layer.Weight,
                                recipeTotalWeight,
                              )}
                              commodityColor={commodityColor}
                              bgColor={getBgColorWithAlpha(
                                commodityColor,
                                0.15,
                              )}
                              isLastInGroup={groupFrame?.position === "last"}
                              isCommodityDisabled={isDisabled}
                            />
                          </LayerGroupBox>
                        );
                      })
                    )}
                  </Box>
                  <Grid
                    container
                    sx={{
                      borderTop: 1,
                      borderColor: "divider",
                      bgcolor: "grey.50",
                      p: "8px 15px",
                      alignItems: "center",
                    }}
                  >
                    <Grid item md={8} xs={12} sx={{ pr: 2 }}></Grid>
                    <Grid item md={4} xs={12} sx={{ textAlign: "right" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "nowrap",
                          fontWeight: "bold",
                        }}
                      >
                        Total: {recipeTotalWeight}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Section Three: Comments */}
          <Box sx={{ px: 2 }}>
            <Controller
              name="Comments"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Box
                  onBlur={() => {
                    if (
                      field.value !== null &&
                      field.value !== currentCharge?.Comments
                    ) {
                      handleFieldChange("Comments", field.value);
                    }
                  }}
                >
                  <CommentsField
                    value={field.value ?? ""}
                    onChange={(val: string) => {
                      field.onChange(val);
                    }}
                    fieldError={error}
                    getValues={getValues}
                    rows={{ minRows: 1, maxRows: 1 }}
                    disabled={
                      modal === "delete" || (isNewCharge && !bucketsSelected)
                    }
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
      )}

      <ChangeStatusScheduledModal
        showStatusChangeConfirmation={showStatusChangeConfirmation}
        handleConfirmStatusChange={handleConfirmStatusChange}
        handleCancelStatusChange={handleCancelStatusChange}
        chargeLayers={chargeLayers}
      />

      <LayerMoveDialog {...layerMoveDialogProps} />

      <ChargeLayerModal
        open={showChargeLayerModal}
        onClose={() => setShowChargeLayerModal(false)}
        onSave={handleSaveNewLayerModal}
        isLoading={isMutating}
        title="Add New Charge Layer"
        chargeId={charge?.ChargeId || null}
        layers={chargeLayers}
      />

      <ConfirmationDialogRaw
        title="Confirmation"
        message={confirmMessage}
        open={showConfirmModal}
        onClose={(confirmed) => {
          setShowConfirmModal(false);
          if (confirmed === "OK") {
            if (pendingRecipe) {
              handleFieldChange("RecipeId", pendingRecipe.id);
              setPendingRecipe(null);
            }
          } else {
            setPendingRecipe(null);
          }
        }}
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={SYerror} onHide={() => SYSetError("")} />
      <ErrorModal error={error} onHide={() => setError("")} />
    </Paper>
  );
};
